import { Ban, Check, KeyRound, Loader2, Lock, LockOpen, MapPin, Users } from "lucide-react";
import { useEffect, useState } from "react";

import { AnswerCompare } from "@/components/AnswerCompare";
import { GroupLeanings, GroupScores, TotalScore } from "@/components/scores";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { Link } from "@/lib/router";
import { cn } from "@/lib/utils";
import { flagFor, languageLabel } from "@/shared/basics";
import { GENDERS } from "@/shared/basics";
import type { PublicProfile as PublicProfileT, UnlockResult } from "@/shared/types";

interface Payload {
  profile: PublicProfileT;
  signedIn: boolean;
}

/**
 * The password prompt, and what it opens.
 *
 * Nothing is remembered. The result lives in this component's state and a
 * reload asks again, which is the whole storage design: there is no cookie to
 * steal, no grant to revoke, and nothing left behind for the next person to use
 * the same browser. The cost is retyping it, which is a fair price for a page
 * that shows somebody's answers.
 */
function Unlock({
  slug,
  theirName,
  onUnlocked,
}: {
  slug: string;
  theirName: string;
  onUnlocked: (result: UnlockResult) => void;
}) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!password.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      onUnlocked(
        await api.post<UnlockResult>(`/api/u/${encodeURIComponent(slug)}/unlock`, {
          password,
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "That didn't work.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="mt-5 border-t border-border/60 pt-4">
      <div className="flex items-start gap-2.5">
        <KeyRound className="mt-0.5 size-4 shrink-0 text-brand" />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            {theirName} set a password for this page. If they gave it to you, it opens the
            answers behind these percentages — beside your own, on every question you have both
            answered.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Input
              type="password"
              value={password}
              autoComplete="off"
              placeholder="Password"
              aria-label={`The password ${theirName} gave you`}
              onChange={(e) => setPassword(e.target.value)}
              className="h-10 min-w-0 flex-1 basis-40 bg-background/60 text-[14px]"
            />
            <Button
              type="submit"
              disabled={busy || !password.trim()}
              className="h-10 shrink-0 rounded-full px-5"
            >
              {busy ? <Loader2 className="animate-spin" /> : <LockOpen />}
              Open
            </Button>
          </div>
          {error && <p className="mt-2 text-[12.5px] text-destructive">{error}</p>}
        </div>
      </div>
    </form>
  );
}

function Gate({ ok, label, detail }: { ok: boolean; label: string; detail?: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <span
        className={cn(
          "mt-0.5 grid size-4 shrink-0 place-items-center rounded-full",
          ok ? "bg-brand/15 text-brand" : "bg-muted text-muted-foreground",
        )}
      >
        {ok ? <Check className="size-2.5" /> : <Ban className="size-2.5" />}
      </span>
      <div className="min-w-0">
        <span className={cn("text-[13.5px]", !ok && "text-muted-foreground")}>{label}</span>
        {detail && (
          <span className="ml-1.5 text-[12.5px] text-muted-foreground/80">{detail}</span>
        )}
      </div>
    </div>
  );
}

export function PublicProfile({ slug }: { slug: string }) {
  const [data, setData] = useState<Payload | null>(null);
  const [unlocked, setUnlocked] = useState<UnlockResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Navigating to a different profile drops whatever the last one opened,
    // rather than carrying one person's answers onto another's page.
    setUnlocked(null);
    api
      .get<Payload>(`/api/u/${encodeURIComponent(slug)}`)
      .then((body) => !cancelled && setData(body))
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <Shell>
        <div className="flex min-h-[60vh] items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" /> Loading…
        </div>
      </Shell>
    );
  }

  if (error || !data) {
    return (
      <Shell>
        <div className="flex min-h-[65vh] flex-col justify-center py-16">
          <h1 className="text-4xl font-semibold tracking-[-0.03em]">No profile here.</h1>
          <p className="mt-4 max-w-lg text-[15.5px] leading-relaxed text-muted-foreground">
            That link doesn't lead anywhere. Either it was never a profile, or its owner has since
            switched sharing off — we don't distinguish between the two on purpose.
          </p>
          <div className="mt-8">
            <Button asChild className="rounded-full px-6">
              <Link href="/">Back to the start</Link>
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  const { profile, signedIn } = data;
  const compat = profile.compatibility;
  const gender = GENDERS.find((g) => g.id === profile.gender);
  const place = [profile.locationText, profile.countryName].filter(Boolean).join(", ");

  return (
    <Shell
      nav={
        !signedIn ? (
          <Button asChild size="sm" className="rounded-full">
            <Link href="/join">Get your own</Link>
          </Button>
        ) : (
          <Button asChild variant="ghost" size="sm" className="rounded-full text-[13px]">
            <Link href="/profile">Your profile</Link>
          </Button>
        )
      }
    >
      <div className="py-12">
        {profile.isSelf && (
          <div className="mb-6 rounded-2xl border border-brand/25 bg-brand/8 px-4 py-3 text-[13.5px] text-muted-foreground">
            This is your own public page, near enough as a stranger sees it. Signed-in visitors
            also see how well the two of you score, and{" "}
            {profile.hasPassword ? (
              <>
                — since you've set a password — whoever you give it to sees your answers beside
                their own.
              </>
            ) : (
              <>nothing else; your answers stay put until you set a password for this link.</>
            )}{" "}
            The one part below marked as yours alone is the exception either way.
          </div>
        )}

        {/* Identity */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-semibold tracking-[-0.03em] sm:text-5xl">
              {profile.name}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[14.5px] text-muted-foreground">
              {profile.age !== null && <span>{profile.age}</span>}
              {gender && (
                <span>
                  <span aria-hidden className="mr-1">
                    {gender.emoji}
                  </span>
                  {gender.label}
                </span>
              )}
              {place && (
                <span className="inline-flex items-center gap-1.5">
                  {profile.countryCode && (
                    <span aria-hidden>{flagFor(profile.countryCode)}</span>
                  )}
                  <MapPin className="size-3.5" />
                  {place}
                </span>
              )}
            </div>
          </div>
        </div>

        {profile.languages.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2">
            {profile.languages.map((l) => (
              <span
                key={l}
                className="rounded-full border border-border/70 bg-card/60 px-3 py-1 text-[13px]"
              >
                {languageLabel(l)}
              </span>
            ))}
          </div>
        )}

        {profile.publicBio && (
          <p className="mt-6 max-w-2xl whitespace-pre-wrap text-[16px] leading-relaxed">
            {profile.publicBio}
          </p>
        )}

        <p className="mt-5 text-[13px] text-muted-foreground">
          <Users className="mr-1.5 inline size-3.5" />
          {profile.answeredCount} questions answered · here since{" "}
          {new Date(profile.memberSince).toLocaleDateString(undefined, {
            month: "long",
            year: "numeric",
          })}
        </p>

        {/* Compatibility */}
        <div className="mt-12">
          {!signedIn ? (
            <div className="rounded-3xl border border-border/70 bg-card/50 p-7 backdrop-blur-sm">
              <Lock className="size-[19px] text-brand" />
              <h2 className="mt-3 text-[19px] font-semibold tracking-tight">
                Sign in to see how you two line up.
              </h2>
              <p className="mt-2.5 max-w-xl text-[15px] leading-relaxed text-muted-foreground">
                Anyone with a profile of their own can open this page and see a compatibility
                score with {profile.name}, broken down by area — just how close the two of you
                are, not what either of you said.
                {profile.hasPassword && (
                  <>
                    {" "}
                    {profile.name} has also set a password. With it, and a profile of your own,
                    the page shows their answers next to yours on every question you have both
                    answered.
                  </>
                )}
              </p>
              <Button asChild className="mt-6 rounded-full px-6">
                <Link href="/join">Create a profile</Link>
              </Button>
            </div>
          ) : profile.isSelf ? (
            <>
              <h2 className="text-[13px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                Where you sit
              </h2>

              <div className="mt-5 rounded-2xl border border-border/70 bg-card/50 p-5 backdrop-blur-sm">
                <div className="mb-4 flex items-baseline justify-between gap-3">
                  <span className="text-[12px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
                    By area
                  </span>
                  <span className="text-[12px] text-muted-foreground">
                    average of your own answers
                  </span>
                </div>

                <GroupLeanings groups={profile.selfLeanings ?? []} />

                <p className="mt-5 border-t border-border/60 pt-3.5 text-[12.5px] leading-relaxed text-muted-foreground">
                  <Lock className="mr-1.5 inline size-3.5" />
                  This block is yours alone — it's the one thing on this page a visitor never
                  sees, because an average over a handful of answers gives the answers away.
                  Scoring you against yourself would tell neither of us anything, so where a
                  visitor sees a compatibility breakdown, you see how far each area pulls you and
                  how much of it you've filled in.
                </p>
              </div>
            </>
          ) : compat === null || compat.total === null ? (
            <div className="rounded-3xl border border-border/70 bg-card/50 p-7 backdrop-blur-sm">
              <h2 className="text-[19px] font-semibold tracking-tight">
                Not enough overlap yet.
              </h2>
              <p className="mt-2.5 max-w-xl text-[15px] leading-relaxed text-muted-foreground">
                You and {profile.name} have only both answered {compat?.overlap ?? 0} of the same
                questions — too few to say anything honest. Answer more sliders on{" "}
                <Link
                  href="/profile"
                  className="underline underline-offset-4 hover:text-foreground"
                >
                  your profile
                </Link>{" "}
                and this will fill in.
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-[13px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                You and {profile.name}
              </h2>

              {/* Unlocked, the answers want the full width, so the two-column
                  split collapses and the score and the gates go side by side
                  above them instead of stacked beside a narrow panel. */}
              <div
                className={cn(
                  "mt-5 grid gap-6 sm:items-start",
                  !unlocked && "sm:grid-cols-[minmax(0,260px)_1fr]",
                )}
              >
                <div
                  className={cn(
                    "space-y-4",
                    unlocked && "sm:grid sm:grid-cols-2 sm:gap-4 sm:space-y-0",
                  )}
                >
                  <TotalScore score={compat.total} />

                  <div className="rounded-2xl border border-border/70 bg-card/50 p-4 backdrop-blur-sm">
                    <div className="mb-2.5 text-[12px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
                      Hard filters
                    </div>
                    <div className="space-y-2">
                      <Gate ok={compat.gates.gender} label="Gender preferences line up" />
                      <Gate ok={compat.gates.age} label="Each of you is in the other's age range" />
                      <Gate
                        ok={compat.gates.language}
                        label="You share a language"
                        detail={
                          compat.gates.sharedLanguages.length > 0
                            ? compat.gates.sharedLanguages.map(languageLabel).join(", ")
                            : undefined
                        }
                      />
                    </div>
                    <p className="mt-3 border-t border-border/60 pt-3 text-[12px] leading-relaxed text-muted-foreground">
                      {compat.gates.passes
                        ? "All three pass, so the matcher can pair you if the score is high enough."
                        : "One of these fails, so however well you score the matcher will never introduce you."}
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl border border-border/70 bg-card/50 p-5 backdrop-blur-sm sm:p-6">
                  <div className="mb-4 flex items-baseline justify-between gap-3">
                    <span className="text-[12px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
                      {unlocked ? "What you each said" : "By area"}
                    </span>
                    <span className="text-[12px] text-muted-foreground">
                      {compat.overlap} shared answers
                    </span>
                  </div>

                  {unlocked ? (
                    <AnswerCompare
                      rows={unlocked.comparison}
                      groups={compat.groups}
                      theirName={profile.name}
                      withheld={unlocked.withheld}
                    />
                  ) : (
                    <>
                      <GroupScores groups={compat.groups} />
                      {profile.hasPassword ? (
                        <Unlock
                          slug={profile.slug}
                          theirName={profile.name}
                          onUnlocked={setUnlocked}
                        />
                      ) : (
                        <p className="mt-5 border-t border-border/60 pt-3.5 text-[12.5px] leading-relaxed text-muted-foreground">
                          Percentages only. {profile.name} hasn't set a password for this page,
                          so the answers behind these numbers stay put — a match would reveal a
                          bio, and both of you saying yes reveals contact details.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Shell>
  );
}
